import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, Subscription, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, tap, switchMap, catchError } from 'rxjs/operators';
import { FireflyService } from '../../services/firefly.service';
import { ExpressSdkService } from '../../services/express-sdk.service';
import { ToastService } from '../../services/toast.service';
import { UtilityService } from "../../services/utility.service";
import { FireflyConfigService } from '../../services/firefly-config.service';
import { LogService } from '../../services/log.service';
import { FireflyGenerationOptions } from '../../models/firefly-common.model';
import { I18nService } from '../../i18n/i18n.service';
import { SharedModule } from '../shared/shared.module';
import { ServiceIcon } from '../shared/page-header/page-header.component';
import { SceneItem, StoryboardSceneComponent } from './storyboard-scene/storyboard-scene.component';
import { StoryboardSettings, StoryboardSettingsComponent } from './storyboard-settings/storyboard-settings.component';

@Component({
  selector: 'app-storyboard-generator',
  standalone: true,
  imports: [CommonModule, FormsModule, SharedModule, StoryboardSettingsComponent, StoryboardSceneComponent],
  templateUrl: './storyboard-generator.component.html',
  styleUrls: ['./storyboard-generator.component.scss']
})
export class StoryboardGeneratorComponent implements OnInit {
  // ヘッダーアイコン設定
  headerIcons: ServiceIcon[] = [
    { src: '/assets/icon_firefly.svg', alt: 'Firefly' },
    { src: '/assets/icon_express.svg', alt: 'Express' }
  ];

  scenes: SceneItem[] = [];
  private textSubjects: { [key: number]: Subject<string> } = {};
  private subscriptions: Subscription[] = [];

  // 詳細設定関連
  showSettings: boolean = false;
  updatedImageUrl: string | null = null;
  currentSettings: StoryboardSettings = {
    styleStrength: 0,
    structureStrength: 0,
    visualIntensity: 0,
    stylePresets: [],
    styleFileId: null
  };

  constructor(
    private fireflyService: FireflyService,
    private expressSdkService: ExpressSdkService,
    private toastService: ToastService,
    private utilityService: UtilityService,
    private fireflyConfigService: FireflyConfigService,
    private logService: LogService,
    public i18n: I18nService
  ) { }

  ngOnInit(): void {
    this.initializeScenes(3);
    document.addEventListener('touchmove', this.preventTouchScroll, { passive: false });
  }

  ngOnDestroy(): void {
    document.removeEventListener('touchmove', this.preventTouchScroll);
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private preventTouchScroll(event: TouchEvent): void {
    if (event.target instanceof HTMLCanvasElement) {
      event.preventDefault();
    }
  }

  initializeScenes(count: number) {
    for (let i = 0; i < count; i++) {
      this.addScene();
    }
  }

  addScene() {
    const id = this.scenes.length;
    this.scenes.push({
      id,
      textValue: '',
      pictures: [],
      loading: false,
      isUploading: false,
      canvasContext: undefined,
      isDrawing: false,
      lastX: 0,
      lastY: 0,
    });

    this.textSubjects[id] = new Subject<string>();
    const subscription = this.textSubjects[id].pipe(
      debounceTime(2000),
      distinctUntilChanged()
    ).subscribe(value => {
      if (value.length > 0) {
        const canvasElement = this.getCanvasElement(id);
        if (!canvasElement) {
          console.error(`Canvas not found for Scene ID: ${id}`);
          return;
        }

        this.generateSceneImage(id, this.scenes[id], this.currentSettings.styleFileId, canvasElement);
      }
    });

    this.subscriptions.push(subscription);
  }

  generateSceneImage(id: number, scene: SceneItem, styleFileId: string | null, canvasElement: HTMLCanvasElement | undefined) {
    if (!canvasElement) {
      this.toastService.showToast(this.i18n.t('toast.structureUploadError'), 'error');
      return;
    }

    if (scene.isUploading) return;

    scene.isUploading = true;
    scene.loading = true;

    const fileBlob = this.utilityService.dataURLToBlob(canvasElement.toDataURL('image/png'));

    // Captured from the generate-async request body to populate the log's `model` field.
    let requestedModelVersion: string | undefined;

    this.fireflyService.uploadFile(fileBlob).pipe(
      tap(resp => {
        console.log('Structure image upload response:', resp);
      }),
      switchMap(resp => {
        if (!resp.fileId) {
          scene.isUploading = false;
          scene.loading = false;
          this.toastService.showToast(this.i18n.t('toast.structureUploadError'), 'error');
          return of(null);
        }

        const seed = Math.floor(Math.random() * 999999999) + 1;

        // 生成オプションを構築
        const options: FireflyGenerationOptions = {
          contentClass: 'art',
          numVariations: 1,
          modelVersion: 'image3',
          seed: seed,
          prompt: `${scene.textValue}`,
          promptBiasingLocaleCode: 'ja-JP',
          width: 2688,
          height: 1536,
          structureImageId: resp.fileId,
          structureStrength: this.currentSettings.structureStrength,
          stylePresets: this.currentSettings.stylePresets,
          styleStrength: this.currentSettings.styleStrength,
          visualIntensity: this.currentSettings.visualIntensity
        };

        // スタイル参照画像の設定
        if (styleFileId) {
          options.styleImageId = styleFileId;
        }

        // FireflyConfigServiceを使用してリクエストを構築
        const requestBody = this.fireflyConfigService.buildGenerationRequest(options);
        requestedModelVersion = requestBody.modelVersion;

        return this.fireflyService.generateAsync(requestBody);
      }),
      catchError(error => {
        scene.isUploading = false;
        scene.loading = false;
        this.toastService.showToast(error.message, 'error');
        return of(null);
      })
    ).subscribe(response => {
      if (!response) {
        scene.isUploading = false;
        scene.loading = false;
        return;
      }

      if (!response.success || !response.jobId) {
        scene.isUploading = false;
        scene.loading = false;
        this.toastService.showToast(this.i18n.t('toast.jobStartFailed'), 'error');
        return;
      }

      const jobId = response.jobId;

      console.log(`シーン ${id} の画像生成ジョブが開始されました。JobID: ${jobId}`);

      this.sendGenerateContentLog(requestedModelVersion);

      this.fireflyService.pollJobStatus(jobId).subscribe({
        next: (statusResponse) => {
          if (statusResponse.status === 'succeeded') {
            console.log(`シーン ${id} の画像生成が完了しました:`, statusResponse);

            if (statusResponse.result && statusResponse.result.outputs && statusResponse.result.outputs.length > 0) {
              scene.pictures = statusResponse.result.outputs.map((output: any) => ({ url: output.image.url }));
            } else {
              this.toastService.showToast(this.i18n.t('toast.imageResultEmpty'), 'error');
            }

            scene.isUploading = false;
            scene.loading = false;
          }

          if (statusResponse.status === 'failed') {
            console.error(`シーン ${id} の画像生成に失敗しました:`, statusResponse);
            this.toastService.showToast(this.i18n.t('toast.imageGenFailed', { detail: statusResponse.message || this.i18n.t('toast.unknownError') }), 'error');

            scene.isUploading = false;
            scene.loading = false;
          }
        },
        error: (error) => {
          console.error(`シーン ${id} のジョブ監視中にエラーが発生しました:`, error);
          this.toastService.showToast(this.i18n.t('toast.jobMonitorError', { detail: error.message }), 'error');

          scene.isUploading = false;
          scene.loading = false;
        }
      });
    });
  }

  /**
   * Reusable so other generation calls on this page can report the same
   * `generate_content` event once wired up.
   */
  private sendGenerateContentLog(modelVersion?: string): void {
    this.logService.logGenerateContent({
      tool: 'sketch_to_storyboard',
      contentType: 'image',
      model: modelVersion,
      imageCount: 1
    }).subscribe();
  }

  onTextChange(data: { value: string; index: number }): void {
    this.textSubjects[data.index].next(data.value);
  }

  onCanvasReady(data: { context: CanvasRenderingContext2D; index: number }): void {
    this.scenes[data.index].canvasContext = data.context;
  }

  getCanvasElement(index: number): HTMLCanvasElement | undefined {
    const scene = this.scenes[index];
    if (scene && scene.canvasContext) {
      return (scene.canvasContext as any).canvas;
    }
    return undefined;
  }

  onExpressEdit(sceneId: number): void {
    if (this.expressSdkService && typeof this.expressSdkService.openExpressEditor === 'function') {
      this.expressSdkService.openExpressEditor(sceneId, this.scenes[sceneId]?.pictures[0]?.url || '')
        .then((updatedUrl: string | void) => {
          if (updatedUrl && typeof updatedUrl === 'string') {
            console.log(`Scene ${sceneId} の画像を更新:`, updatedUrl);
            this.scenes[sceneId].pictures[0].url = updatedUrl;
          }
        }).catch(error => console.error('Error updating image after Express Editor:', error));
    } else {
      console.error('openExpressEditor is not defined in ExpressSdkService');
    }
  }

  onSettingsChange(settings: StoryboardSettings): void {
    this.currentSettings = settings;
  }

  onShowSettingsChange(show: boolean): void {
    this.showSettings = show;
  }
}
